import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Save } from 'lucide-react';

import { notifPrefsApi } from '@/features/notifications/api';
import { SmartLoading } from '@/lib/ui';

const CATEGORY_LABELS: Record<string, string> = {
  messages: 'Direct + group messages',
  broadcasts: 'Tenant broadcasts',
  alerts: 'Operational alerts',
  maintenance: 'Maintenance updates',
  advances: 'Advance request decisions',
  payroll: 'Payslip + payroll',
  documents: 'Document expiry',
};

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  push: 'Mobile push',
  in_app: 'In-app',
};

export function NotificationPreferencesPage() {
  const queryClient = useQueryClient();
  const prefs = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: notifPrefsApi.get,
  });

  const [draft, setDraft] = useState<Record<string, Record<string, boolean>>>({});

  useEffect(() => {
    if (prefs.data) setDraft(prefs.data.preferences);
  }, [prefs.data]);

  const save = useMutation({
    mutationFn: () => notifPrefsApi.put(draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });

  if (prefs.isLoading) {
    return <SmartLoading className="p-6 text-sm text-muted-foreground" />;
  }
  if (!prefs.data) {
    return null;
  }

  const channels = prefs.data.channels;
  const categories = prefs.data.categories;

  function toggle(channel: string, category: string, value: boolean) {
    setDraft((d) => ({
      ...d,
      [channel]: { ...(d[channel] ?? {}), [category]: value },
    }));
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(prefs.data.preferences);

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <header className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2.5">
            <Bell className="h-6 w-6 text-primary" />
            Notification preferences
          </h1>
          <p className="page-subtitle">
            Pick which categories reach you on which channel. In-app delivery stays on so badge
            counters keep working.
          </p>
        </div>
        <button
          className="btn-primary"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          <Save className="h-4 w-4" />
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </header>

      <section className="card !p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Category</th>
              {channels.map((c) => (
                <th key={c} className="px-4 py-3 text-center">
                  {CHANNEL_LABELS[c] ?? c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <tr key={cat} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{CATEGORY_LABELS[cat] ?? cat}</td>
                {channels.map((ch) => {
                  const checked = !!draft[ch]?.[cat];
                  return (
                    <td key={ch} className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => toggle(ch, cat, e.target.checked)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {save.isError && (
        <p className="text-sm text-red-500">Could not save preferences. Try again.</p>
      )}
    </div>
  );
}
